import { QueryClient } from '@tanstack/react-query';
import isNumber from 'lodash/isNumber';

import { getUserHostingQueryKey } from '@newfold/huapi-js';
import {
  HostingAccount200ResourcesStorage,
  HostingInfoDiskUsage200,
} from '@newfold/huapi-js/src/index.schemas';

import { RouteLevel } from '~/components/Breadcrumbs';
import { BannerConfig } from '~/hooks/useInfoBanners';

import {
  DISABLE_STATUSES,
  MAX_FILE_LIMIT,
  USAGE_ERR_PERCENTAGE,
  USAGE_WARN_PERCENTAGE,
  VIDEO_ARTICLE_CARD_CONFIG,
} from './constants';
import {
  NullableNumber,
  NullableString,
  UsageTypes,
  VideoArticleCardConfigTypes,
} from './types';

/**
 * Converts WN.HP. format to WN-HP- format by replacing dots with hyphens
 * @param hostingId - The hosting ID string to check and convert
 * @returns The converted string with hyphens, or the original string if no conversion needed
 */
export function convertDotsToHyphens(hostingId: string): string {
  if (hostingId.includes('WN.HP.')) {
    return hostingId.replace(/\./g, '-');
  }
  return hostingId;
}

/**
 * Converts WN-HP- format to WN.HP. format by replacing hyphens with dots
 * @param hostingId - The hosting ID string to check and convert
 * @returns The converted string with dots, or the original string if no conversion needed
 */
export function convertHyphensToDots(hostingId: string): string {
  if (hostingId.includes('WN-HP-')) {
    return hostingId.replace(/-/g, '.');
  }
  return hostingId;
}

export const getHostingDisabled = (status: string | null | undefined) => {
  // returns `true` by default to prevent a false positive from returning before the parent fuction completes, letting whatever element that's rendered by this function flash as `enabled` then `disabled` if/when a truthy value returns
  if (!status) return true;
  const lowerCaseStatus = status?.toLowerCase();
  return DISABLE_STATUSES.includes(lowerCaseStatus);
};

export const getSelectedHostingId = (
  id?: NullableString,
  addon_back_ref?: NullableString,
): NullableString | undefined => {
  return window.location.pathname.includes('hal/mfe_mysites')
    ? id
    : addon_back_ref;
};

export const getUsageStats = (data?: HostingInfoDiskUsage200) => {
  let percentUsed = 0,
    filePercentUsed = 0;
  let variant = 'primary',
    fileVariant = 'primary';
  let hasIcon = false,
    hasFileIcon = false;

  if (data && data?.disklimit !== 'Unlimited') {
    const used = data?.diskused!;
    const limit = data?.disklimit!;
    percentUsed = Number(+used.slice(0, -2) / +limit.slice(0, -2)) * 100;
    variant = 'primary';
    switch (true) {
      case percentUsed >= USAGE_ERR_PERCENTAGE:
        variant = 'error';
        hasIcon = true;
        break;
      case percentUsed < USAGE_ERR_PERCENTAGE &&
        percentUsed >= USAGE_WARN_PERCENTAGE:
        variant = 'warning';
        break;
      default:
        variant = 'success';
    }
  }

  if (data && data?.fileslimit !== MAX_FILE_LIMIT) {
    filePercentUsed = Number(data?.filesused! / data?.fileslimit!) * 100;
    fileVariant = 'primary';
    switch (true) {
      case filePercentUsed >= USAGE_ERR_PERCENTAGE:
        fileVariant = 'error';
        hasFileIcon = true;
        break;
      case filePercentUsed < 90 && filePercentUsed >= USAGE_WARN_PERCENTAGE:
        fileVariant = 'warning';
        break;
      default:
        fileVariant = 'success';
    }
  }

  const alertState = getAlertState(percentUsed, filePercentUsed);

  const stats = {
    percentUsed,
    variant,
    filePercentUsed,
    fileVariant,
    hasIcon,
    hasFileIcon,
    alertState,
  } as UsageTypes;

  return stats;
};

export const getAlertState = (
  diskPercentUsed: number,
  filePercentUsed: number,
) => {
  const diskUsed = diskPercentUsed ?? 0;
  const fileUsed = filePercentUsed ?? 0;

  if (diskUsed >= 100 || fileUsed >= 100) return 'critical';
  if (diskUsed > 98 || fileUsed > 98) return 'warning';
  if (diskUsed >= 90 || fileUsed >= 90) return 'alert';

  return null;
};

export const calculateMaxStorageAllowed = ({
  siteStorageTotal = null,
  storageAvailable = null,
  siteStorageQuota,
}: {
  /**
   * The total storage allocated to the site.
   */
  siteStorageTotal?: NullableNumber;
  /**
   * The additional storage available for the site.
   */
  storageAvailable?: NullableNumber;
  siteStorageQuota: number;
}): number => {
  if (isNumber(siteStorageTotal) && isNumber(storageAvailable)) {
    return Number(siteStorageTotal) + Number(storageAvailable);
  }

  return isNumber(storageAvailable) && storageAvailable <= siteStorageQuota
    ? storageAvailable
    : siteStorageQuota;
};

export const getValidBreadcrumbLevels = (levels: RouteLevel[]) => {
  return levels.reduce<RouteLevel[]>((acc, level) => {
    const trimmedLabel = level.label.trim();

    if (!trimmedLabel) {
      return acc;
    }

    acc.push({ ...level, label: trimmedLabel });
    return acc;
  }, []);
};

const BANNER_PRIORITY: Record<string, number> = {
  error: 0,
  custom: 0,
  warning: 1,
  info: 2,
};

/**
 * Returns only the highest-priority banner from the provided list.
 * Priority order: error = custom > warning > info
 */
export const getHighestPriorityBanner = (
  banners: BannerConfig[],
): BannerConfig[] => {
  const getPriority = (banner: BannerConfig) =>
    BANNER_PRIORITY[banner.severity] ?? 99;

  const highestPriorityBanner = banners.reduce<BannerConfig | null>(
    (highestSoFar, candidate) =>
      highestSoFar === null ||
      getPriority(candidate) < getPriority(highestSoFar)
        ? candidate
        : highestSoFar,
    null,
  );

  return highestPriorityBanner ? [highestPriorityBanner] : [];
};

export const getVideoArticleCardConfig = ({
  hasAiStore,
  hasAiSite,
  aiStoreArticleUrl,
  aiSiteArticleUrl,
}: VideoArticleCardConfigTypes) => {
  const selectedConfigType = hasAiStore
    ? 'aiStore'
    : hasAiSite
    ? 'aiSite'
    : null;
  if (!selectedConfigType) return null;
  return {
    ...VIDEO_ARTICLE_CARD_CONFIG[selectedConfigType],
    articleUrl: hasAiStore ? aiStoreArticleUrl : aiSiteArticleUrl,
  };
};
export const getSiteStorageQuotaLimit = (
  storageInfo: HostingAccount200ResourcesStorage | undefined,
) => {
  return isNumber(storageInfo?.site_quota)
    ? Number(storageInfo?.site_quota)
    : 250;
};

export const removeHostingListQueries = (queryClient: QueryClient) => {
  queryClient.removeQueries({
    queryKey: getUserHostingQueryKey(),
  });
};
